import Article from "./Article";
import ArticleCollection from "./ArticleCollection";
import { generateObjectId, toObjectIdHex } from "../utils/objectId";
import Space, { Configurator, Source } from "../composition/Space";
import Toggles from "@domain/toggles/Toggles";
import { isComposingPendantsCategory, isCreaConfigurator, isDrawingConfigurator, isNoDrawingConfigurator } from "@domain/utils/utils";
import Price from "./Price";

const CONFIGURATOR_IDS = {
    'CREA': 1,
    'EXPRESS': 4,
    'COMPOSITOR': 5,
    'ATTRIBUTES_CONFIGURATOR': 6
}

interface ProjectionOptions {
    withPrice: boolean,
    priceRate: number,
    discounts?: {
        [subLevelId: number]: number
    },
    articleCollection: ArticleCollection
}
export interface ConfiguratorType {
    id: 4 | 5 | 6,
    description: Configurator,
    source: Source
}

const getOID = (value) => {
    let oid = value
    if (oid.$oid) {
        oid = oid.$oid
    } else if (oid.toString) {
        oid = oid.toString()
    }

    return oid
}
export default class BaseBOM {
    props;

    constructor(props) {
        this.props = props;
        if (this.props && !this.props?._id) {
            this.props._id = generateObjectId()
        } else if (this.props?._id) {
            this.props._id = toObjectIdHex(getOID(this.props._id))
        }
    }

    getProps() {
        return JSON.parse(JSON.stringify(this.props))
    }

    getProp(prop) {
        return this.props[prop]
    }

    setId(id: string) {
        this.props._id = toObjectIdHex(id)
    }

    getProductId() {
        return this.props._id.toString()
    }

    setPreconfigId(preconfigId: string) {
        this.props.preconfigId = preconfigId
    }

    setConfigId(configId: string) {
        this.props.configId = configId
    }

    setQuantity(quantity: number) {
        if (quantity > 0)
            this.props.quantity = quantity;
    }  
    
    getSublevel() {
        return this.getProps().sublevel?.id || '632'
    }

    getType() {
        return this.getProps().type
    }

    getReferenceLeadtime(articleCollection: ArticleCollection, reference: string, quantity: number) {
        const article = articleCollection.getArticle(reference)
        const leadTimeFunction = article?.getProps()?.leadtime || []
        let leadtime = null
        for (const leadTimeRange of leadTimeFunction) {
            if (leadTimeRange.from <= quantity) {
                leadtime = leadTimeRange.weeks
            }
        }
        return leadtime
    }

    getAvailabilityDate(articleCollection: ArticleCollection) {
        return 0
    }

    // TODO: Consider isPersonalized value
    isValid(articleCollection: ArticleCollection) {
        const orderReferences = this.getOrderReferences(articleCollection)
        const moq = []
        let valid = true
        for (const orderRef of orderReferences) {
            if (orderRef.moq > orderRef.quantity) {
                valid = false
            }
            moq.push({
                reference: orderRef.reference,
                moq: orderRef.moq
            })
        }
        return {
            valid,
            moq
        }
    }

    getReferenceMoq(articleCollection: ArticleCollection, reference: string) {
        const article = articleCollection?.getArticle(reference)
        return article?.getProps()?.moq
    }

    getLeadtime(articleCollection: ArticleCollection) {
        const orderReferences = this.getOrderReferences(articleCollection)
        let leadtime = null
        for (const orderReference of orderReferences) {
            if (!leadtime || leadtime < orderReference.leadtime) {
                leadtime = orderReference.leadtime
            }
        }
        return leadtime
    }

    getOrderReferences(_articleCollection?: ArticleCollection) {
        return []
    }

    getModels() {
        return []
    }

    setCreationNumber(_creationNumber: number) {
        return this
    }

    // returns 'articleCodes' or 'creationCodes' that have been allocated to this BOM.
    // articleCodes are special, individual codes, that are given to certain articles.
    // creationCodes are special, individual codes, that are given to type:CREATION BOMs
    getAllocatedCodes() {
        return []
    }

    refreshCodes(_articleCollection, _allocatedCodes, counters) {
        return { BOM: this, counters }
    }

    getArticle(articleCollection: ArticleCollection, articleProp: any): Article {
        const article = articleCollection.getArticle(articleProp.referenceCode)

        if (articleProp.priceReference) {
            article.props.priceReference = articleProp.priceReference
        }
        if (!article) return null
        if (articleProp.attributes?.pendantHeight?.value) {
            article.setPendantHeight(articleProp.attributes.pendantHeight.value / 100)
        }
        return article
    }

    getArticles(_articleCollection?: ArticleCollection) {
        return []
    }

    private calculateArticlePrice(article, options, sublevelId, isComposition = false) {
        const { articleCollection, priceRate, discounts } = options
        let articlePrice = 0
        try {
            const priceObj = articleCollection.getArticlePrice(article, priceRate)
            articlePrice = priceObj.getSalePrice()
            if (!isComposition && (!Toggles.isEnabled("noRoundPlusminusEssentials") || !priceObj.isPlusminus())) {
                articlePrice = Price.getRoundedSalePrice(articlePrice)
            }
        } catch (error) {
            console.error('error while trying to fetch article price from articleCollection')
        }
        const discountPercent = discounts?.[sublevelId] || 0
        const discount = articlePrice * (discountPercent / 100)
        let netPrice = 0
        try {
            netPrice = Number((articlePrice - discount).toFixed(2))
        } catch (error) {
            console.error('error while trying to fetch net price')
        }


        return {
            articlePrice,
            netPrice,
            discount,
        }
    }

    private calculateAndAssignArticlePrice(item, options, sublevelId, isComposition = false) {
        const { articleCollection } = options;
        let articleClass = this.getArticle(articleCollection, item);
        const { discount, articlePrice, netPrice } = this.calculateArticlePrice(articleClass, options, sublevelId, isComposition);

        item.prices = {
            totalDiscount: discount,
            totalGross: articlePrice * item.quantity,
            totalNet: netPrice * item.quantity,
            unitGross: articlePrice,
            unitNet: netPrice,
            unitDiscount: discount,
        };

        item.isAvailablePrice = 1;
        item.isPersonalized = +articleClass.isPersonalized();
        return item
    }

    getProjection(options: ProjectionOptions) {
        // Return the product JSON with a prices key, 
        //     - If calculation method is known, it will be calculated. 
        //     - Otherwise it will return price = 0 or the previously provided value
        // TODO: Add the new configuratorType when it is created (5 = VIBIA COMPOSITOR)
        let projection = {
            ...this.getProps()
        }

        if (options.withPrice && options.priceRate && options.articleCollection) {
            const CONFIGURATOR_IDS_PROJECTION = Object.values(CONFIGURATOR_IDS).filter(id => id != CONFIGURATOR_IDS.CREA)
            if (CONFIGURATOR_IDS_PROJECTION.includes(this.props.configuratorType?.id)) {
                const articles = this.getArticles(options.articleCollection)
                projection.prices = {
                    totalDiscount: 0,
                    totalGross: 0,
                    totalNet: 0
                }
                for (const article of articles) {
                    const sublevel = this.getSublevel();
                    const { discount, articlePrice, netPrice } = this.calculateArticlePrice(article, options, sublevel)
                    projection.prices = {
                        totalDiscount: projection.prices.totalDiscount + discount,
                        totalGross: projection.prices.totalGross + articlePrice,
                        totalNet: projection.prices.totalNet + netPrice
                    }
                }
            } else if (!projection.prices) {
                projection.prices = {
                    totalDiscount: 0,
                    totalGross: 0,
                    totalNet: 0
                }
            }
            projection.prices = {
                ...projection.prices,
                unitGross: projection.prices.totalGross / (projection.quantity || 1),
                unitNet: projection.prices.totalNet / (projection.quantity || 1),
                unitDiscount: projection.prices.totalDiscount / (projection.quantity || 1),
            }
            if (Array.isArray(projection?.compositions)) {
                projection.prices = {
                    ...projection.prices,
                    totalDiscount: 0,
                    totalGross: 0,
                    totalNet: 0,
                    unitDiscount: 0,
                    unitGross: 0,
                    unitNet: 0,
                }

                for (const composition of projection.compositions) {
                    const isCrea = isCreaConfigurator(projection.configuratorType)
                    const isDrawing = isDrawingConfigurator(projection.configuratorType)
                    const isNoDrawing = isNoDrawingConfigurator(projection.configuratorType)

                    const notCalculateInDomain = isCrea || isDrawing || isNoDrawing

                    const notCompositionCP = !isComposingPendantsCategory(composition.category)

                    if (Toggles.isEnabled("refactorDomainPrices")) {
                        if (notCalculateInDomain) {
                            projection.prices.totalDiscount += composition.prices.unitDiscount
                            projection.prices.totalGross += composition.prices.unitGross
                            projection.prices.totalNet += composition.prices.unitNet
                            continue
                        }
                    } else {
                        projection.prices.totalDiscount += composition.prices.unitDiscount
                        projection.prices.totalGross += composition.prices.unitGross
                        projection.prices.totalNet += composition.prices.unitNet
                        continue
                    }

                    composition.prices = {
                        totalDiscount: 0,
                        totalGross: 0,
                        totalNet: 0,
                        unitGross: 0,
                        unitNet: 0,
                        unitDiscount: 0
                    }

                    const articles = []
                    const isProductComposingPendant = Toggles.isEnabled("dontCalcPriceAccesorieWhenCP") ? composition.category?.subcategory?.id == 1 : false
                    if (composition.accessories && composition.accessories.length > 0 && !isProductComposingPendant) {
                        articles.push(...composition.accessories)
                    }
                    if (composition.articles && composition.articles.length > 0) {
                        articles.push(...composition.articles)
                    }
                    for (const article of articles) {
                        const item = this.calculateAndAssignArticlePrice(article, options, this.getSublevel(), true);
                        item.currency = composition.currency;

                        // Suma los precios al total de la composición
                        composition.prices = {
                            totalDiscount: composition.prices.totalDiscount + item.prices.unitDiscount * item.quantity,
                            totalGross: composition.prices.totalGross + item.prices.unitGross * item.quantity,
                            totalNet: composition.prices.totalNet + item.prices.unitNet * item.quantity,
                            unitGross: composition.prices.totalGross + item.prices.unitGross * item.quantity,
                            unitNet: composition.prices.totalNet + item.prices.unitNet * item.quantity,
                            unitDiscount: composition.prices.totalDiscount + item.prices.unitDiscount * item.quantity,
                        };
                    }

                    projection.prices = {
                        ...projection.prices,
                        totalDiscount: (composition.prices.totalDiscount + projection.prices.totalDiscount),
                        totalGross: (composition.prices.totalGross + projection.prices.totalGross),
                        totalNet: (composition.prices.totalNet + projection.prices.totalNet)
                    }
                }

                projection.prices.unitDiscount = projection.prices.totalDiscount
                projection.prices.unitGross = projection.prices.totalGross
                projection.prices.unitNet = projection.prices.totalNet
                projection.prices.totalDiscount *= projection.quantity
                projection.prices.totalGross *= projection.quantity
                projection.prices.totalNet *= projection.quantity
            }
        }


        return projection
    }

    static calculateCode(prefix = 'XXXXLW', allocatedCodes = [], counter = 0, calcCounter = true) {
        if (allocatedCodes?.length) {
            const code = allocatedCodes[0]
            return {
                code,
                number: parseInt(code.slice(prefix.length), 10),
                allocatedCodes: allocatedCodes.slice(1),
                counter
            }
        } else {
            const seq = calcCounter ? counter + 1 : counter
            let code;
            if (!prefix.includes("LW")) {
                code = `${prefix}${seq.toString()}`
            } else {
                code = `${prefix}${seq.toString().padStart(9, '0')}`
            }
            return {
                code,
                number: parseInt(code.slice(prefix.length), 10),
                allocatedCodes: [],
                counter: seq
            }
        }
    }

    static getConfiguratorType(space: Space): ConfiguratorType {
        const source = space?.getSource() || 'PRIVATE_AREA'
        const configurator = space?.getConfigurator() || 'EXPRESS'

        let configuratorType: ConfiguratorType = {
            id: CONFIGURATOR_IDS[configurator] as 4 | 5 | 6,
            description: configurator,
            source
        }
        return configuratorType
    }

    static parseArticleProperties(article: Article) {
        const articleData = article.getProps()

        let BOM = {
            model: articleData.model,
            referenceCode: articleData.reference,
            referenceBase: articleData.referenceBase,
            attributes: articleData.attributes,
            lumensLed: articleData.lumensLed,
            watts: articleData.watts,
            sublevel: articleData.sublevel,
            familyId: articleData.family.id, // El bom quiere el family ID unicamente
            subFamily: articleData.subFamily, // Sobra el CODE (Lo dejamos de extra)
            hasAttributes: articleData.hasAttributes, // Falta installation -> !! Hablar con Alejandro para que se permita la instalación // TODO
            applicationLocation: articleData.applicationsLocations && articleData.applicationsLocations.length > 0 ? articleData.applicationsLocations[0] : null,
            elementOrderValue: articleData.elementOrderValue
        }

        /* Parsing values to expected */
        if (BOM.attributes?.ledColourTemp?.value) {
            BOM.attributes.ledColourTemp.value = Number.parseInt(BOM.attributes.ledColourTemp.value)
        }

        if (BOM.applicationLocation) {
            delete BOM.applicationLocation.application.shortCode
            delete BOM.applicationLocation.location.shortCode
        }

        delete BOM.attributes?.control?.isOptional
        delete BOM.attributes?.control?.optional
        delete BOM.attributes?.finish?.isOptional
        delete BOM.attributes?.ledColourTemp?.isOptional

        delete BOM.lumensLed?.efficiency
        delete BOM.subFamily.code
        delete BOM.watts.optionalValue

        return BOM
    }
}